/**
 * A self-contained QR Code encoder (byte mode).
 *
 * The guest clients need to turn the venue's `WIFI:T:WPA;S:…;P:…;;` payload
 * into something a phone camera can actually read. Neither client had a QR
 * dependency and neither could take one cheaply — customer-web is a PWA we keep
 * small, and the native app resolves its shared code through npm `file:` links where
 * a new transitive dependency is a rebuild of the whole tree. So the encoder
 * lives here and returns nothing but a module matrix; the renderer (inline SVG
 * on web, react-native-svg on native) is the only platform-specific half.
 *
 * Implements ISO/IEC 18004 byte mode, which is all a Wi-Fi or URL payload
 * needs. The table constants and the structure follow the public-domain
 * reference design by Project Nayuki.
 */

export type QrEcc = 'L' | 'M' | 'Q' | 'H';

/** Row index into the tables below. */
const ECC_INDEX: Record<QrEcc, number> = { L: 0, M: 1, Q: 2, H: 3 };

/** The two-bit code written into the format information — a different order. */
const ECC_FORMAT_BITS: Record<QrEcc, number> = { L: 1, M: 0, Q: 3, H: 2 };

const MIN_VERSION = 1;
const MAX_VERSION = 40;

/** ECC codewords per block, indexed [eccIndex][version]; index 0 is unused. */
const ECC_CODEWORDS_PER_BLOCK: number[][] = [
  // 1   2   3   4   5   6   7   8   9  10  11  12  13  14  15  16  17  18  19  20  21  22  23  24  25  26  27  28  29  30  31  32  33  34  35  36  37  38  39  40
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
];

/** Number of ECC blocks, indexed [eccIndex][version]. */
const NUM_ECC_BLOCKS: number[][] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
];

// --------------------------------------------------------------- helpers

/** UTF-8 by hand: Hermes cannot be relied on to expose `TextEncoder`. */
function toUtf8(text: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < text.length; i++) {
    let code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00);
        i++;
      }
    }
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    else if (code < 0x10000)
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    else
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
  }
  return out;
}

function numRawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

function numDataCodewords(version: number, ecc: QrEcc): number {
  const e = ECC_INDEX[ecc];
  return (
    Math.floor(numRawDataModules(version) / 8) -
    ECC_CODEWORDS_PER_BLOCK[e][version] * NUM_ECC_BLOCKS[e][version]
  );
}

/** Byte-mode character-count field width, which widens at version 10. */
const charCountBits = (version: number): number => (version < 10 ? 8 : 16);

function appendBits(bits: number[], value: number, length: number): void {
  for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
}

// ------------------------------------------------------ Reed–Solomon (GF 256)

function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMultiply(result[j], root);
      if (j + 1 < degree) result[j] ^= result[j + 1];
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const byte of data) {
    const factor = byte ^ (result.shift() as number);
    result.push(0);
    for (let i = 0; i < divisor.length; i++) result[i] ^= gfMultiply(divisor[i], factor);
  }
  return result;
}

// ------------------------------------------------------------- the matrix

interface Grid {
  size: number;
  /** true = dark. */
  modules: boolean[][];
  /** Function patterns are immune to masking. */
  reserved: boolean[][];
}

function blankGrid(version: number): Grid {
  const size = version * 4 + 17;
  const make = () => Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  return { size, modules: make(), reserved: make() };
}

function setFunction(grid: Grid, x: number, y: number, dark: boolean): void {
  if (x < 0 || y < 0 || x >= grid.size || y >= grid.size) return;
  grid.modules[y][x] = dark;
  grid.reserved[y][x] = true;
}

function alignmentPositions(version: number): number[] {
  if (version === 1) return [];
  const count = Math.floor(version / 7) + 2;
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (count * 2 - 2)) * 2;
  const result = [6];
  for (let pos = version * 4 + 17 - 7; result.length < count; pos -= step) result.splice(1, 0, pos);
  return result;
}

function drawFinder(grid: Grid, cx: number, cy: number): void {
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      setFunction(grid, cx + dx, cy + dy, distance !== 2 && distance !== 4);
    }
  }
}

function drawAlignment(grid: Grid, cx: number, cy: number): void {
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      setFunction(grid, cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }
}

/** 15-bit format info: 2 ECC bits + 3 mask bits, BCH(15,5) + a fixed XOR mask. */
function drawFormatBits(grid: Grid, ecc: QrEcc, mask: number): void {
  const data = (ECC_FORMAT_BITS[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;

  const bit = (i: number) => ((bits >>> i) & 1) === 1;

  for (let i = 0; i <= 5; i++) setFunction(grid, 8, i, bit(i));
  setFunction(grid, 8, 7, bit(6));
  setFunction(grid, 8, 8, bit(7));
  setFunction(grid, 7, 8, bit(8));
  for (let i = 9; i < 15; i++) setFunction(grid, 14 - i, 8, bit(i));

  for (let i = 0; i < 8; i++) setFunction(grid, grid.size - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) setFunction(grid, 8, grid.size - 15 + i, bit(i));
  setFunction(grid, 8, grid.size - 8, true); // the always-dark module
}

/** 18-bit version info, BCH(18,6). Only from version 7 up. */
function drawVersionBits(grid: Grid, version: number): void {
  if (version < 7) return;
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  const bits = (version << 12) | rem;

  for (let i = 0; i < 18; i++) {
    const dark = ((bits >>> i) & 1) === 1;
    const a = grid.size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunction(grid, a, b, dark);
    setFunction(grid, b, a, dark);
  }
}

function drawFunctionPatterns(grid: Grid, version: number, ecc: QrEcc): void {
  // Timing patterns first — the finders overwrite their own corners anyway.
  for (let i = 0; i < grid.size; i++) {
    setFunction(grid, 6, i, i % 2 === 0);
    setFunction(grid, i, 6, i % 2 === 0);
  }

  drawFinder(grid, 3, 3);
  drawFinder(grid, grid.size - 4, 3);
  drawFinder(grid, 3, grid.size - 4);

  const positions = alignmentPositions(version);
  for (let i = 0; i < positions.length; i++) {
    for (let j = 0; j < positions.length; j++) {
      // The three finder corners have no alignment pattern.
      const corner =
        (i === 0 && j === 0) ||
        (i === 0 && j === positions.length - 1) ||
        (i === positions.length - 1 && j === 0);
      if (!corner) drawAlignment(grid, positions[i], positions[j]);
    }
  }

  drawFormatBits(grid, ecc, 0); // reserves the cells; rewritten with the real mask
  drawVersionBits(grid, version);
}

/** Zigzag placement, two columns at a time, right to left, skipping column 6. */
function drawCodewords(grid: Grid, data: number[]): void {
  let i = 0;
  for (let right = grid.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < grid.size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? grid.size - 1 - vert : vert;
        if (!grid.reserved[y][x] && i < data.length * 8) {
          grid.modules[y][x] = ((data[i >>> 3] >>> (7 - (i & 7))) & 1) === 1;
          i++;
        }
        // Remaining modules past the data are left light, as the spec requires.
      }
    }
  }
}

function maskAt(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0: return (x + y) % 2 === 0;
    case 1: return y % 2 === 0;
    case 2: return x % 3 === 0;
    case 3: return (x + y) % 3 === 0;
    case 4: return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5: return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6: return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default: return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
  }
}

function applyMask(grid: Grid, mask: number): void {
  for (let y = 0; y < grid.size; y++) {
    for (let x = 0; x < grid.size; x++) {
      if (!grid.reserved[y][x] && maskAt(mask, x, y)) grid.modules[y][x] = !grid.modules[y][x];
    }
  }
}

/** ISO/IEC 18004 mask penalty rules — lower is better. */
function penaltyScore(grid: Grid): number {
  const { size, modules } = grid;
  let score = 0;

  const scanLine = (get: (i: number) => boolean) => {
    let lineScore = 0;
    let runColor = get(0);
    let runLength = 1;
    for (let i = 1; i < size; i++) {
      const value = get(i);
      if (value === runColor) {
        runLength++;
      } else {
        if (runLength >= 5) lineScore += runLength - 2;
        runColor = value;
        runLength = 1;
      }
    }
    if (runLength >= 5) lineScore += runLength - 2;

    return lineScore;
  };

  for (let y = 0; y < size; y++) score += scanLine((x) => modules[y][x]);
  for (let x = 0; x < size; x++) score += scanLine((y) => modules[y][x]);

  // Rule 2 — 2×2 blocks of one colour.
  for (let y = 0; y + 1 < size; y++) {
    for (let x = 0; x + 1 < size; x++) {
      const c = modules[y][x];
      if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) {
        score += 3;
      }
    }
  }

  // Rule 3 — the finder-like 1:1:3:1:1 pattern with a light margin.
  const pattern = [true, false, true, true, true, false, true];
  const matches = (get: (i: number) => boolean, at: number, quietBefore: boolean) => {
    for (let k = 0; k < 7; k++) if (get(at + k) !== pattern[k]) return false;
    const quiet = (from: number) => {
      for (let k = 0; k < 4; k++) {
        const i = from + k;
        if (i >= 0 && i < size && get(i)) return false;
      }
      return true;
    };
    return quietBefore ? quiet(at - 4) : quiet(at + 7);
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x + 7 <= size; x++) {
      const get = (i: number) => (i >= 0 && i < size ? modules[y][i] : false);
      if (matches(get, x, true)) score += 40;
      if (matches(get, x, false)) score += 40;
    }
  }
  for (let x = 0; x < size; x++) {
    for (let y = 0; y + 7 <= size; y++) {
      const get = (i: number) => (i >= 0 && i < size ? modules[i][x] : false);
      if (matches(get, y, true)) score += 40;
      if (matches(get, y, false)) score += 40;
    }
  }

  // Rule 4 — deviation from a 50/50 dark ratio.
  let dark = 0;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (modules[y][x]) dark++;
  const total = size * size;
  const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
  score += Math.max(0, k) * 10;

  return score;
}

// ------------------------------------------------------------------ encode

function buildCodewords(bytes: number[], version: number, ecc: QrEcc): number[] {
  const capacityBits = numDataCodewords(version, ecc) * 8;
  const bits: number[] = [];
  appendBits(bits, 0b0100, 4); // byte mode
  appendBits(bits, bytes.length, charCountBits(version));
  for (const b of bytes) appendBits(bits, b, 8);

  // Terminator, then pad to a byte boundary, then the alternating pad bytes.
  appendBits(bits, 0, Math.min(4, capacityBits - bits.length));
  appendBits(bits, 0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) appendBits(bits, pad, 8);

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j];
    data.push(byte);
  }
  return data;
}

/** Split into blocks, add ECC to each, then interleave as the spec requires. */
function addEccAndInterleave(data: number[], version: number, ecc: QrEcc): number[] {
  const e = ECC_INDEX[ecc];
  const numBlocks = NUM_ECC_BLOCKS[e][version];
  const eccLen = ECC_CODEWORDS_PER_BLOCK[e][version];
  const rawCodewords = Math.floor(numRawDataModules(version) / 8);
  const shortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortLength = Math.floor(rawCodewords / numBlocks);

  const divisor = rsDivisor(eccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dataLength = shortLength - eccLen + (i < shortBlocks ? 0 : 1);
    const block = data.slice(k, k + dataLength);
    k += dataLength;
    const blockEcc = rsRemainder(block, divisor);
    // Short blocks get a placeholder so every block is the same length; the
    // interleaver skips that one column, which is what keeps the ECC columns
    // in step across blocks of two different sizes.
    if (i < shortBlocks) block.push(0);
    blocks.push(block.concat(blockEcc));
  }

  const result: number[] = [];
  for (let i = 0; i < blocks[0].length; i++) {
    for (let j = 0; j < blocks.length; j++) {
      if (i !== shortLength - eccLen || j >= shortBlocks) result.push(blocks[j][i]);
    }
  }
  return result;
}

export interface QrMatrix {
  /** Modules per side, excluding the quiet zone. */
  size: number;
  /** `modules[y][x]` — true is a dark module. */
  modules: boolean[][];
}

/**
 * Encode `text` as a QR Code and return its module matrix.
 *
 * Throws only when the payload cannot fit in a version-40 symbol, which for a
 * Wi-Fi payload cannot happen — the callers therefore treat a throw as "render
 * nothing" rather than as an error worth showing a guest.
 */
export function encodeQr(text: string, ecc: QrEcc = 'M'): QrMatrix {
  const bytes = toUtf8(text);

  let version = MIN_VERSION;
  for (; version <= MAX_VERSION; version++) {
    const capacityBits = numDataCodewords(version, ecc) * 8;
    if (4 + charCountBits(version) + bytes.length * 8 <= capacityBits) break;
  }
  if (version > MAX_VERSION) throw new Error('Payload is too long for a QR code');

  const codewords = addEccAndInterleave(buildCodewords(bytes, version, ecc), version, ecc);

  const grid = blankGrid(version);
  drawFunctionPatterns(grid, version, ecc);
  drawCodewords(grid, codewords);

  // Try all eight masks and keep the one the spec's penalty rules like best.
  let bestMask = 0;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(grid, mask);
    drawFormatBits(grid, ecc, mask);
    const score = penaltyScore(grid);
    if (score < bestScore) {
      bestScore = score;
      bestMask = mask;
    }
    applyMask(grid, mask); // XOR is its own inverse — undo before the next try
  }
  applyMask(grid, bestMask);
  drawFormatBits(grid, ecc, bestMask);

  return { size: grid.size, modules: grid.modules };
}

/**
 * The module matrix as a single SVG path `d` attribute — one `M…h1v1h-1z` per
 * dark module. One path beats thousands of `<rect>` nodes on a phone.
 */
export function qrPath(matrix: QrMatrix): string {
  const parts: string[] = [];
  for (let y = 0; y < matrix.size; y++) {
    for (let x = 0; x < matrix.size; x++) {
      if (matrix.modules[y][x]) parts.push(`M${x} ${y}h1v1h-1z`);
    }
  }
  return parts.join('');
}
